// ============================================================================
// BitAssay — Data Export Utilities
// ============================================================================
// Pure conversion functions for exporting data in multiple formats.

/**
 * Convert an array of objects (or primitives) to CSV format.
 */
export function toCSV(data: Record<string, unknown>[] | number[], columns?: string[]): string {
  if (!data.length) return ''

  // Handle plain number arrays
  if (typeof data[0] === 'number') {
    const header = columns?.[0] ?? 'value'
    const lines = [header]
    for (const v of data as number[]) {
      lines.push(String(v))
    }
    return lines.join('\n')
  }

  const rows = data as Record<string, unknown>[]
  const headers = columns ?? Object.keys(rows[0]!)
  const lines = [headers.join(',')]
  for (const row of rows) {
    const values = headers.map((h) => {
      const v = row[h]
      if (v === null || v === undefined) return ''
      if (typeof v === 'number') return v.toString()
      if (typeof v === 'boolean') return v ? 'true' : 'false'
      if (typeof v === 'string') {
        const escaped = v.replace(/"/g, '""')
        // Prevent formula injection: prefix with tab if starts with =, +, -, @
        if (/^[=+\-@]/.test(escaped)) return `"\t${escaped}"`
        return `"${escaped}"`
      }
      return String(v)
    })
    lines.push(values.join(','))
  }
  return lines.join('\n')
}

/**
 * Convert an array of objects (or primitives) to TSV format.
 */
export function toTSV(data: Record<string, unknown>[] | number[], columns?: string[]): string {
  if (!data.length) return ''

  if (typeof data[0] === 'number') {
    const header = columns?.[0] ?? 'value'
    const lines = [header]
    for (const v of data as number[]) {
      lines.push(String(v))
    }
    return lines.join('\n')
  }

  const rows = data as Record<string, unknown>[]
  const headers = columns ?? Object.keys(rows[0]!)
  const lines = [headers.join('\t')]
  for (const row of rows) {
    const values = headers.map((h) => {
      const v = row[h]
      if (v === null || v === undefined) return ''
      if (typeof v === 'number') return v.toString()
      if (typeof v === 'boolean') return v ? 'true' : 'false'
      if (typeof v === 'string') return v.replace(/\t/g, ' ')
      return String(v)
    })
    lines.push(values.join('\t'))
  }
  return lines.join('\n')
}

/**
 * Convert data to pretty-printed JSON.
 */
export function toJSON(data: unknown[]): string {
  return JSON.stringify(data, null, 2)
}

/**
 * Escape special LaTeX characters in a string.
 */
function escapeLatex(str: string): string {
  return str
    .replace(/\\/g, '\\textbackslash{}')
    .replace(/[&%$#_{}]/g, (m) => '\\' + m)
    .replace(/~/g, '\\textasciitilde{}')
    .replace(/\^/g, '\\textasciicircum{}')
}

/**
 * Convert data to a LaTeX tabular environment.
 */
export function toLaTeX(
  data: Record<string, unknown>[] | number[],
  columns?: string[],
  title?: string,
): string {
  if (!data.length) return ''

  const lines: string[] = []

  // Handle plain number arrays
  if (typeof data[0] === 'number') {
    const header = columns?.[0] ?? 'Value'
    lines.push('\\begin{table}[htbp]')
    lines.push('\\centering')
    if (title) lines.push(`\\caption{${escapeLatex(title)}}`)
    lines.push('\\begin{tabular}{r}')
    lines.push('\\hline')
    lines.push(`\\textbf{${escapeLatex(header)}} \\\\`)
    lines.push('\\hline')
    for (const v of data as number[]) {
      lines.push(`${v} \\\\`)
    }
    lines.push('\\hline')
    lines.push('\\end{tabular}')
    lines.push('\\end{table}')
    return lines.join('\n')
  }

  const rows = data as Record<string, unknown>[]
  const headers = columns ?? Object.keys(rows[0]!)

  // Determine column alignment: 'l' for strings, 'r' for numbers
  const alignments = headers.map((h) => {
    const firstVal = rows.find((r) => r[h] !== null && r[h] !== undefined)?.[h]
    return typeof firstVal === 'number' ? 'r' : 'l'
  })

  lines.push('\\begin{table}[htbp]')
  lines.push('\\centering')
  if (title) lines.push(`\\caption{${escapeLatex(title)}}`)
  lines.push(`\\begin{tabular}{${alignments.join('')}}`)
  lines.push('\\hline')
  lines.push(headers.map((h) => `\\textbf{${escapeLatex(h)}}`).join(' & ') + ' \\\\')
  lines.push('\\hline')

  for (const row of rows) {
    const cells = headers.map((h) => {
      const v = row[h]
      if (v === null || v === undefined) return '---'
      if (typeof v === 'number') {
        if (v < 0.001 && v > 0) return v.toExponential(3)
        if (Number.isInteger(v)) return v.toString()
        return v.toFixed(6)
      }
      if (typeof v === 'boolean') return v ? 'Pass' : 'Fail'
      return escapeLatex(String(v))
    })
    lines.push(cells.join(' & ') + ' \\\\')
  }

  lines.push('\\hline')
  lines.push('\\end{tabular}')
  lines.push('\\end{table}')
  return lines.join('\n')
}

/**
 * Generate a Python/NumPy code snippet for the data.
 */
export function toPython(
  data: Record<string, unknown>[] | number[],
  columns?: string[],
): string {
  if (!data.length) return '# No data'

  const lines: string[] = ['import numpy as np', '']

  // Handle plain number arrays
  if (typeof data[0] === 'number') {
    const label = columns?.[0] ?? 'data'
    lines.push(`# ${label}`)
    lines.push(`data = np.array([`)
    const nums = data as number[]
    // Print in chunks of 8 for readability
    for (let i = 0; i < nums.length; i += 8) {
      const chunk = nums.slice(i, i + 8)
      lines.push('    ' + chunk.map((v) => String(v)).join(', ') + ',')
    }
    lines.push('])')
    return lines.join('\n')
  }

  const rows = data as Record<string, unknown>[]
  const headers = columns ?? Object.keys(rows[0]!)
  lines.push(`# columns: ${headers.join(', ')}`)
  lines.push('data = np.array([')
  for (const row of rows) {
    const values = headers.map((h) => {
      const v = row[h]
      if (v === null || v === undefined) return 'np.nan'
      if (typeof v === 'number') return String(v)
      if (typeof v === 'boolean') return v ? 'True' : 'False'
      return `"${String(v).replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/"/g, '\\"')}"`
    })
    lines.push(`    [${values.join(', ')}],`)
  }
  lines.push('])')
  return lines.join('\n')
}

/**
 * Trigger a file download in the browser.
 */
export function downloadString(
  content: string,
  fileName: string,
  mimeType: string,
): void {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Convert a plain number array into export-ready objects with index and label.
 */
export function arrayToExportData(
  values: number[],
  label: string,
): { index: number; [key: string]: number }[] {
  return values.map((v, i) => ({ index: i, [label]: v }))
}

/**
 * Normalize XY-style data (may use various key names) to standard x/y format.
 */
export function xyToExportData(
  data: { x?: number; y?: number; theoretical?: number; observed?: number }[],
): { x: number; y: number }[] {
  return data.map((d) => ({
    x: d.x ?? d.theoretical ?? 0,
    y: d.y ?? d.observed ?? 0,
  }))
}
